import { formatMoneyShort } from '@/api/boundaryAdapter';
import type {
  FlagKey,
  KpiItem,
  MonthDayRecord,
  MonthWeekSummary,
  MonthGridCell,
  Provider,
  Appointment,
  WeekAppointment,
  WeekDay,
  CalendarWeekTitleRange,
} from '@elva/ui';
import { formatWeekTitleRange } from '@elva/ui';
import type {
  CalendarDayCellRaw,
  CalendarDayMetaProvider,
  CalendarDayOperatory,
  CalendarDayProvider,
  CalendarDayResponse,
  CalendarDayResponseRaw,
  CalendarCell,
  CalendarMonthResponse,
  CalendarMonthResponseRaw,
  CalendarMonthSummary,
  CalendarMonthSummaryRaw,
  CalendarNext7Response,
  CalendarNext7ResponseRaw,
  DayKpiItem,
  MonthDay,
  MonthDayRaw,
  Next7Cell,
  Next7CellRaw,
  Next7Day,
  Next7DayRaw,
  WeekKpiItem,
} from '../api/types';

const PROVIDER_COLORS = [
  '#8E3567',
  '#4D2855',
  '#D8625F',
  '#D49545',
  '#0F5C45',
  '#2563EB',
  '#7C3AED',
  '#0891B2',
];

// Keys are the backend flag name with all underscores stripped (see normalizeFlagKey).
// The backend (organization/frontdesk_core/flags.py) emits camelCase full words —
// e.g. `eligibilityFail`, `formsIncomplete`, `eligibilityOk` — NOT the abbreviated
// forms. Both the abbreviated and full-word aliases are listed so a flag survives
// regardless of which the backend sends. Missing a full-word alias silently drops
// the flag (it resolves to null), which is why FD91's real eligibility/forms flags
// need these entries.
const FLAG_ALIASES: Record<string, FlagKey> = {
  medalert: 'med_alert',
  // eligibility
  eligok: 'elig_ok',
  eligibilityok: 'elig_ok',
  eligfail: 'elig_fail',
  eligibilityfail: 'elig_fail',
  // forms
  formsok: 'forms_ok',
  formscomplete: 'forms_ok',
  formsinc: 'forms_inc',
  formsincomplete: 'forms_inc',
  // pre-auth
  preauthp: 'preauth_p',
  preauthpending: 'preauth_p',
  // patient markers
  newpt: 'new_pt',
  newpatient: 'new_pt',
  bigcase: 'big_case',
  isvip: 'vip',
  isbirthday: 'birthday',
  // lab / clear
  labmissing: 'lab_missing',
  allclear: 'all_clear',
};

function normalizeFlagKey(raw: string): FlagKey | null {
  const snake = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
  const aliased = FLAG_ALIASES[snake.replace(/_/g, '')] ?? snake;
  return VALID_FLAGS.has(aliased as FlagKey) ? (aliased as FlagKey) : null;
}

/**
 * Appointment state is three independent axes (source of truth):
 *   booking_state      — still on the books?  SCHEDULED / CANCELLED / COMPLETED …
 *   confirmation_state — did the patient confirm?  UNCONFIRMED / CONFIRMED …
 *   visit_state        — where in the visit?  NOT_ARRIVED → ARRIVED → SEATED → CHECKED_OUT …
 * `status` / `confirmed_state` are legacy PMS/UI fields kept only as a fallback,
 * and `flags` are operational alerts (formsIncomplete, triageItems …), not state.
 */
export interface AppointmentAxes {
  status?: string | undefined;
  confirmed_state?: string | undefined;
  booking_state?: string | undefined;
  confirmation_state?: string | undefined;
  visit_state?: string | undefined;
}

function axisKey(value: string | undefined): string {
  return value?.toUpperCase().replace(/[^A-Z]/g, '') ?? '';
}

/** Legacy flat status (calendar/bird-eye vocabulary) derived from the three axes. */
export function deriveAppointmentStatus(raw: AppointmentAxes): string | undefined {
  const booking = axisKey(raw.booking_state);
  const visit = axisKey(raw.visit_state);
  if (!booking && !visit) return raw.status;

  switch (booking) {
    case 'NOSHOW':
      return 'NO_SHOW';
    case 'BROKEN':
    case 'FAILED':
    case 'CANCELLED':
    case 'CANCELED':
      return 'BROKEN';
    case 'RESCHEDULED':
    case 'RESCHED':
      return 'RESCHED';
    case 'COMPLETED':
      return 'COMPLETED';
    default:
      break;
  }

  switch (visit) {
    case 'ARRIVED':
      return 'ARRIVED';
    case 'CHECKEDIN':
      return 'CHECKED_IN';
    case 'SEATED':
    case 'INCHAIR':
    case 'INTREATMENT':
    case 'TREATMENT':
      return 'TREATMENT';
    case 'TREATMENTDONE':
    case 'CHECKEDOUT':
    case 'DEPARTED':
      return 'COMPLETED';
    default:
      break;
  }

  return axisKey(raw.confirmation_state ?? raw.confirmed_state) === 'CONFIRMED'
    ? 'CONFIRMED'
    : 'SCHEDULED';
}

/** `confirmed` / `unconfirmed` per `confirmation_state` (legacy `confirmed_state` as fallback). */
export function deriveConfirmationFlag(raw: AppointmentAxes): 'confirmed' | 'unconfirmed' | null {
  const key = axisKey(raw.confirmation_state ?? raw.confirmed_state);
  if (key === 'CONFIRMED') return 'confirmed';
  if (key === 'UNCONFIRMED') return 'unconfirmed';
  return null;
}

/** The confirmation axis owns confirmed/unconfirmed; drop whatever `flags[]` said about it. */
export function applyConfirmationFlag<T extends string>(
  flags: T[],
  confirmation: 'confirmed' | 'unconfirmed' | null,
): Array<T | 'confirmed' | 'unconfirmed'> {
  if (!confirmation) return flags;
  const rest = flags.filter((f) => {
    const key = normalizeFlagKey(f);
    return key !== 'confirmed' && key !== 'unconfirmed';
  });
  return [...rest, confirmation];
}

function parseProductionCents(value: string | number | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : undefined;
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.round(parsed * 100);
}

function extractLocalHour(isoString: string | undefined): number | undefined {
  if (!isoString) return undefined;
  const match = /T(\d{2}):(\d{2})/.exec(isoString);
  if (match) return Number(match[1]) + Number(match[2]) / 60;
  const d = new Date(isoString);
  return d.getHours() + d.getMinutes() / 60;
}

function initialsFromName(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

function normalizeProcedure(
  procedure: CalendarDayCellRaw['procedure'],
  appointmentReason?: string,
): CalendarCell['procedure'] | undefined {
  if (typeof procedure === 'string' && procedure.trim()) {
    return { name: procedure, cdt: '' };
  }
  if (procedure && typeof procedure === 'object' && procedure.name) {
    return procedure;
  }
  if (appointmentReason?.trim()) {
    return { name: appointmentReason, cdt: '' };
  }
  return undefined;
}

export function normalizeCalendarCell(raw: CalendarDayCellRaw): CalendarCell {
  const appointment_id = raw.appointment_id ?? raw.id;
  const column_id =
    raw.provider_column_id ?? raw.operatory_column_id ?? raw.column_id ?? '';
  const start = raw.start ?? raw.start_local;
  const end = raw.end ?? raw.end_local;
  const duration_min = raw.duration_min ?? raw.duration_minutes;
  const production_cents =
    raw.production_cents ?? parseProductionCents(raw.production);
  const patient =
    raw.patient ??
    (raw.patient_name
      ? { id: raw.patient_id ?? appointment_id, name: raw.patient_name }
      : undefined);
  const flags = applyConfirmationFlag(
    (raw.flags ?? []).map(normalizeFlagKey).filter((f): f is FlagKey => f != null),
    deriveConfirmationFlag(raw),
  );
  const status = deriveAppointmentStatus(raw) ?? raw.status_label;

  const procedure = normalizeProcedure(raw.procedure, raw.appointment_reason);

  return {
    appointment_id,
    column_id,
    ...(raw.provider_column_id ? { provider_column_id: raw.provider_column_id } : {}),
    ...(raw.operatory_column_id ? { operatory_column_id: raw.operatory_column_id } : {}),
    ...(start ? { start } : {}),
    ...(end ? { end } : {}),
    ...(duration_min != null ? { duration_min } : {}),
    ...(patient ? { patient } : {}),
    ...(status ? { status } : {}),
    ...(raw.confirmation_state ? { confirmation_state: raw.confirmation_state } : {}),
    ...(production_cents != null ? { production_cents } : {}),
    ...(procedure ? { procedure } : {}),
    ...(raw.rollup_color ? { rollup_color: raw.rollup_color } : {}),
    ...(flags.length > 0 ? { flags } : {}),
    ...(raw.readiness ? { readiness: raw.readiness } : {}),
    rebuilding: raw.rebuilding ?? false,
  };
}

const ACTION_AXES: Record<string, AppointmentAxes> = {
  ARRIVED: { visit_state: 'ARRIVED' },
  SEATED: { visit_state: 'SEATED' },
  TREATMENT_DONE: { visit_state: 'TREATMENT_DONE' },
  CHECKED_OUT: { visit_state: 'CHECKED_OUT' },
  NO_SHOW: { booking_state: 'NO_SHOW' },
  CANCELLED: { booking_state: 'CANCELLED' },
  RESCHEDULED: { booking_state: 'RESCHEDULED' },
};

/**
 * What a status action does to one cached calendar cell, so the card shows the new state before the
 * server's read model has caught up (a cell still being rebuilt is carried over from the cache).
 */
export function applyStatusActionToCalendarCell(cell: CalendarCell, action: string): CalendarCell {
  if (action === 'CONFIRMED') {
    const status = cell.status?.toUpperCase();
    return {
      ...cell,
      confirmation_state: 'CONFIRMED',
      flags: applyConfirmationFlag(cell.flags ?? [], 'confirmed'),
      ...(cell.readiness ? { readiness: { ...cell.readiness, confirmed: true } } : {}),
      ...(!status || status === 'SCHEDULED' || status === 'PENDING' ? { status: 'CONFIRMED' } : {}),
    };
  }
  const axes = ACTION_AXES[action];
  const status = axes ? deriveAppointmentStatus(axes) : undefined;
  return status ? { ...cell, status } : cell;
}

export function adaptCalendarDayResponse(raw: CalendarDayResponseRaw): CalendarDayResponse {
  const rawCells = raw.cells ?? [];
  const cells = rawCells.map(normalizeCalendarCell);
  const metaProviders = raw.meta?.providers;

  let providers: CalendarDayProvider[];
  if ((raw.providers ?? []).length > 0) {
    providers = raw.providers!;
  } else if (metaProviders?.length) {
    providers = sortMetaProviders(metaProviders.map(normalizeMetaProvider), metaProviders);
  } else {
    providers = deriveProvidersFromCells(rawCells);
  }

  let operatories = (raw.operatories ?? raw.meta?.operatories ?? []).map(
    (o): CalendarDayOperatory => ({
      column_id: o.column_id,
      name: o.name ?? o.display_name ?? o.operatory_name ?? o.column_id,
    }),
  );
  if (operatories.length === 0) {
    operatories = deriveOperatoriesFromCells(rawCells);
  }

  const wireKpis = normalizeDayWireKpis(raw.kpis);
  const renderKpis = raw.render?.kpis?.length ? raw.render.kpis : wireKpis;
  const render =
    raw.render != null
      ? { ...raw.render, kpis: renderKpis }
      : renderKpis.length > 0
        ? { kpis: renderKpis, insights: { tiers: [] } }
        : undefined;

  const dayVersion =
    raw.day_version ??
    (raw.meta?.version != null ? String(raw.meta.version) : '');

  return {
    day_version: dayVersion,
    providers,
    operatories,
    cells,
    ...(render ? { render } : {}),
    ...(raw.summary ? { summary: raw.summary } : {}),
  };
}

function normalizeMetaProvider(p: CalendarDayMetaProvider): CalendarDayProvider {
  return {
    column_id: p.column_id,
    name: p.name ?? p.display_name ?? p.column_id,
    ...(p.role ? { role: p.role } : {}),
    ...(p.initials ? { initials: p.initials } : {}),
    ...(p.color ? { color: p.color } : {}),
    ...(p.production_cents != null ? { production_cents: p.production_cents } : {}),
    ...(p.patient_count != null ? { patient_count: p.patient_count } : {}),
    ...(p.ontime != null ? { ontime: p.ontime } : {}),
  };
}

function sortMetaProviders(
  providers: CalendarDayProvider[],
  metaProviders: CalendarDayMetaProvider[],
): CalendarDayProvider[] {
  const order = new Map(
    metaProviders.map((p, index) => [p.column_id, p.display_order ?? index]),
  );
  return [...providers].sort(
    (a, b) => (order.get(a.column_id) ?? 999) - (order.get(b.column_id) ?? 999),
  );
}

function normalizeDayWireKpis(
  raw: CalendarDayResponseRaw['kpis'],
): DayKpiItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return (raw.kpis ?? []).map((k): DayKpiItem => {
    const sub = k.sub ?? k.target_annotation ?? undefined;
    return {
      key: k.key,
      ...(k.value != null ? { value: k.value } : {}),
      ...(k.value_cents != null ? { value_cents: k.value_cents } : {}),
      ...(k.label ? { label: k.label } : {}),
      ...(k.display_value ? { display_value: k.display_value } : {}),
      ...(k.color ? { color: k.color } : {}),
      ...(k.live ? { live: k.live } : {}),
      ...(k.clickable ? { clickable: k.clickable } : {}),
      ...(k.goal_cents != null ? { goal_cents: k.goal_cents } : {}),
      ...(sub ? { sub } : {}),
    };
  });
}

function deriveOperatoriesFromCells(rawCells: CalendarDayCellRaw[]): CalendarDayOperatory[] {
  const byColumn = new Map<string, CalendarDayOperatory>();
  rawCells.forEach((cell) => {
    const column_id = cell.operatory_column_id;
    if (!column_id || byColumn.has(column_id)) return;
    byColumn.set(column_id, {
      column_id,
      name: cell.operatory_name ?? column_id,
    });
  });
  return [...byColumn.values()];
}

function deriveProvidersFromCells(rawCells: CalendarDayCellRaw[]): CalendarDayProvider[] {
  const byColumn = new Map<string, CalendarDayProvider>();
  rawCells.forEach((cell) => {
    const column_id = cell.provider_column_id ?? cell.operatory_column_id ?? cell.column_id;
    if (!column_id || byColumn.has(column_id)) return;
    byColumn.set(column_id, {
      column_id,
      name: cell.provider_name ?? cell.operatory_name ?? column_id,
    });
  });
  return [...byColumn.values()];
}

export function adaptCalendarProvidersForGrid(
  apiProviders: CalendarDayProvider[],
  cells: CalendarCell[],
): Provider[] {
  const patientCounts = new Map<string, number>();
  const productionCents = new Map<string, number>();
  cells.forEach((cell) => {
    if (!cell.column_id) return;
    patientCounts.set(cell.column_id, (patientCounts.get(cell.column_id) ?? 0) + 1);
    productionCents.set(
      cell.column_id,
      (productionCents.get(cell.column_id) ?? 0) + (cell.production_cents ?? 0),
    );
  });

  return apiProviders.map((p, index) => ({
    id: p.column_id,
    name: p.name,
    role: p.role ?? 'Provider',
    initials: p.initials ?? initialsFromName(p.name),
    color: p.color ?? PROVIDER_COLORS[index % PROVIDER_COLORS.length]!,
    production: Math.round((productionCents.get(p.column_id) ?? p.production_cents ?? 0) / 100),
    patients: p.patient_count ?? patientCounts.get(p.column_id) ?? 0,
    ontime: p.ontime ?? 100,
  }));
}

export { extractLocalHour, normalizeFlagKey, parseProductionCents };

const VALID_FLAGS = new Set<FlagKey>([
  'elig_ok', 'forms_ok', 'confirmed', 'reminded', 'all_clear', 'forms_inc',
  'balance', 'preauth_p', 'anxiety', 'new_pt', 'big_case', 'unconfirmed',
  'elig_fail', 'lab_missing', 'med_alert', 'birthday', 'vip',
]);

const VALID_STATUSES = new Set<string>([
  'CONFIRMED', 'COMPLETED', 'CHECKED_IN', 'ARRIVED', 'TREATMENT',
  'SCHEDULED', 'PENDING', 'NO_SHOW', 'BROKEN', 'RESCHED',
]);

function parseHour(time: string): number {
  const isoHour = extractLocalHour(time);
  if (isoHour != null) return isoHour;
  const match = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!match) return 9;
  return Number(match[1]) + Number(match[2]) / 60;
}

export function mapFlags(flags: string[]): FlagKey[] {
  return flags
    .map((f) => normalizeFlagKey(f))
    .filter((f): f is FlagKey => f != null);
}

function todayIsoLocal(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeFillPct(value: number): number {
  return value <= 1 ? Math.round(value * 100) : Math.round(value);
}

function normalizeDensityWire(raw: number[]): number[] {
  if (!raw.length) return [];
  const max = Math.max(...raw);
  if (max <= 1) return raw;
  return raw.map((v) => {
    if (v <= 0) return 0;
    if (v >= max * 0.75) return 1;
    return 0.5;
  });
}

function deriveDensityFromCells(cells: Next7CellRaw[], startHour = 8, slotCount = 11): number[] {
  const counts = new Array<number>(slotCount).fill(0);
  cells.forEach((cell) => {
    const hour = extractLocalHour(cell.start_local ?? cell.start ?? cell.time);
    if (hour == null) return;
    const idx = Math.floor(hour - startHour);
    if (idx >= 0 && idx < slotCount) counts[idx]!++;
  });
  return normalizeDensityWire(counts);
}

function enrichNext7DateFields(
  raw: Next7DayRaw,
  todayIso: string,
): Pick<Next7Day, 'short' | 'num' | 'full' | 'relative' | 'is_today' | 'date'> {
  const date = raw.date;
  if (!date) return {};
  const d = new Date(`${date}T12:00:00`);
  const today = new Date(`${todayIso}T12:00:00`);
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);
  return {
    date,
    short: raw.weekday ?? raw.short ?? d.toLocaleDateString('en-US', { weekday: 'short' }),
    num: raw.num ?? d.getDate(),
    full:
      raw.full ??
      d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    relative:
      raw.relative ??
      (diffDays === 0
        ? 'Today'
        : diffDays === 1
          ? 'Tomorrow'
          : diffDays === -1
            ? 'Yesterday'
            : ''),
    is_today: raw.is_today ?? date === todayIso,
  };
}

function normalizeNext7Cell(raw: Next7CellRaw, dayId: string, cellIndex: number): Next7Cell {
  const appointment_id = raw.appointment_id ?? raw.id ?? cellIndex;
  const production_cents =
    raw.production_cents ?? parseProductionCents(raw.production) ?? 0;
  const patient = raw.patient ?? raw.patient_name ?? '';
  const time = raw.time ?? raw.start_local ?? raw.start ?? '';
  const procedure =
    (typeof raw.procedure === 'string' ? raw.procedure : '') ||
    raw.appointment_reason ||
    '';
  const duration_minutes = raw.duration_minutes ?? raw.duration_min;
  const rawFlags =
    raw.flags?.length
      ? raw.flags
      : raw.highest_severity_flag
        ? [raw.highest_severity_flag]
        : [];

  return {
    appointment_id,
    time,
    patient,
    status: deriveAppointmentStatus(raw) ?? raw.status_label ?? 'SCHEDULED',
    production_cents,
    flags: applyConfirmationFlag(rawFlags, deriveConfirmationFlag(raw)),
    provider: raw.provider ?? raw.provider_name ?? '',
    ...(raw.provider_column_id ? { provider_column_id: raw.provider_column_id } : {}),
    procedure,
    ...(duration_minutes != null ? { duration_minutes } : {}),
  };
}

function normalizeNext7Day(raw: Next7DayRaw, dayIndex: number): Next7Day {
  const stats = raw.stats;
  const dateFields = enrichNext7DateFields(raw, todayIsoLocal());
  const cells = raw.cells ?? [];

  const production_cents =
    raw.production_cents ??
    parseProductionCents(raw.production) ??
    parseProductionCents(stats?.production_total) ??
    0;

  const fill_pct =
    raw.fill_pct ??
    (stats?.fillPct != null ? normalizeFillPct(stats.fillPct) : undefined);

  const densityRaw = raw.density ?? stats?.density;
  const density =
    densityRaw?.length
      ? normalizeDensityWire(densityRaw)
      : cells.length
        ? deriveDensityFromCells(cells)
        : [];

  const at_risk = raw.at_risk ?? stats?.red_count;
  const new_patients = raw.new_patients ?? stats?.newPatients;
  const open_slots = raw.open_slots ?? (stats?.open != null ? stats.open : undefined);

  return {
    ...dateFields,
    ...(raw.closed != null ? { closed: raw.closed } : {}),
    ...(fill_pct != null ? { fill_pct } : {}),
    density,
    production_cents,
    ...(at_risk != null ? { at_risk } : {}),
    ...(new_patients != null ? { new_patients } : {}),
    ...(open_slots != null ? { open_slots } : {}),
    cells: cells.map((cell, cellIndex) => normalizeNext7Cell(cell, `d${dayIndex}`, cellIndex)),
  };
}

export function adaptCalendarNext7Response(raw: CalendarNext7ResponseRaw): CalendarNext7Response {
  const kpis = normalizeDayWireKpis(raw.kpis);
  return {
    days: (raw.days ?? []).map(normalizeNext7Day),
    week_kpis: raw.week_kpis ?? [],
    ...(kpis.length ? { kpis } : {}),
    ...(raw.meta ? { meta: raw.meta } : {}),
    ...(raw.summary ? { summary: raw.summary } : {}),
  };
}

function mapStatus(status: string): WeekAppointment['status'] {
  const raw = status.toUpperCase();
  return VALID_STATUSES.has(raw) ? (raw as WeekAppointment['status']) : 'SCHEDULED';
}

function formatNext7DayLabel(day: Next7Day): string {
  if (day.full) return day.full;
  const parts = [day.short, day.num != null ? String(day.num) : ''].filter(Boolean);
  return parts.join(' ');
}

export function adaptNext7Days(days: Next7Day[]): WeekDay[] {
  return days.map((day, index) => {
    const densityOpen = (day.density ?? []).filter((d) => d === 0).length;
    return {
      id: `d${index}`,
      short: day.short ?? '',
      num: day.num ?? 0,
      full: formatNext7DayLabel(day),
      relative: day.relative ?? '',
      isToday: day.is_today ?? false,
      closed: day.closed ?? false,
      density: day.density ?? [],
      fillPct: day.fill_pct ?? 0,
      production: Math.round((day.production_cents ?? 0) / 100),
      atRisk: day.at_risk ?? 0,
      open: day.open_slots ?? densityOpen,
      newPatients: day.new_patients ?? 0,
    };
  });
}

export function adaptWeekAppointments(days: Next7Day[]): WeekAppointment[] {
  const appts: WeekAppointment[] = [];
  days.forEach((day, dayIndex) => {
    const dayId = `d${dayIndex}`;
    (day.cells ?? []).forEach((cell, cellIndex) => {
      const appointmentId = cell.appointment_id ?? cellIndex;
      appts.push({
        id: String(appointmentId),
        dayId,
        hour: parseHour(cell.time ?? ''),
        providerId: cell.provider_column_id ?? cell.provider ?? 'unknown',
        patient: cell.patient ?? '',
        status: mapStatus(cell.status ?? 'SCHEDULED'),
        production: Math.round((cell.production_cents ?? 0) / 100),
        procedure: cell.procedure ?? '',
        flags: mapFlags(cell.flags ?? []),
      });
    });
  });
  return appts;
}

export function weekAppointmentToAppointment(appt: WeekAppointment): Appointment {
  return {
    id: appt.id,
    providerId: appt.providerId,
    hour: appt.hour,
    duration: 1,
    patient: appt.patient,
    status: appt.status,
    production: appt.production,
    procedure: appt.procedure,
    flags: appt.flags,
  };
}

const DAY_KPI_MAP: Record<string, { label: string; icon: KpiItem['icon']; accent: string }> = {
  production_captured: { label: 'Production captured', icon: 'dollar', accent: 'var(--success)' },
  projected: { label: 'Projected vs goal', icon: 'bars', accent: 'var(--gold)' },
  projected_vs_goal: { label: 'Projected vs goal', icon: 'bars', accent: 'var(--gold)' },
  day_progress: { label: 'Day progress', icon: 'clock', accent: 'var(--ai-3)' },
  no_shows: { label: 'No-shows today', icon: 'alert', accent: 'var(--terracotta)' },
  new_patients: { label: 'New patients today', icon: 'team', accent: 'var(--ai-4)' },
  new_patients_today: { label: 'New patients today', icon: 'team', accent: 'var(--ai-4)' },
  same_day_opps: { label: 'Same-day opps', icon: 'bolt', accent: 'var(--ai-5)' },
  same_day_fills: { label: 'Same-day fills today', icon: 'bolt', accent: 'var(--ai-5)' },
  booked_pct: { label: 'Booked %', icon: 'bars', accent: 'var(--gold)' },
  open_slots: { label: 'Open slots remaining', icon: 'stopwatch', accent: 'var(--gold)' },
  production_scheduled: { label: 'Production scheduled', icon: 'dollar', accent: 'var(--success)' },
  confirmations: { label: 'Confirmations', icon: 'check', accent: 'var(--success)' },
  confirmations_pending: { label: 'Confirmations pending', icon: 'check', accent: 'var(--success)' },
  open_slots_week: { label: 'Open slots this week', icon: 'stopwatch', accent: 'var(--gold)' },
  week_booking_pct: { label: 'Week booking %', icon: 'bars', accent: 'var(--gold)' },
  week_production: { label: 'Week production', icon: 'dollar', accent: 'var(--success)' },
  hygiene_capacity: { label: 'Hygiene capacity', icon: 'clock', accent: 'var(--ai-3)' },
  new_patients_week: { label: 'New patients this week', icon: 'team', accent: 'var(--ai-4)' },
  same_day_fills_trailing: { label: 'Same-day fills (last 7d)', icon: 'bolt', accent: 'var(--ai-5)' },
  hygiene_utilization: { label: 'Hygiene chair utilization', icon: 'clock', accent: 'var(--ai-3)' },
  production_posted: { label: 'Production posted', icon: 'dollar', accent: 'var(--success)' },
};

function kpiColorToAccent(color?: string): string {
  switch (color?.toLowerCase()) {
    case 'green':
      return 'var(--success)';
    case 'yellow':
      return 'var(--gold)';
    case 'red':
      return 'var(--terracotta)';
    default:
      return 'var(--ink-2)';
  }
}

export function adaptDayKpis(kpis: DayKpiItem[]): KpiItem[] {
  return kpis.map((k) => {
    const meta = DAY_KPI_MAP[k.key] ?? {
      label: k.label ?? k.key.replace(/_/g, ' '),
      icon: 'dollar' as const,
      accent: kpiColorToAccent(k.color),
    };

    const primary =
      k.display_value ??
      (k.value_cents != null
        ? formatMoneyShort(k.value_cents)
        : k.value != null
          ? String(k.value)
          : '—');

    let subTone: KpiItem['subTone'];
    if (k.goal_cents != null && k.value_cents != null) {
      subTone = k.value_cents >= k.goal_cents ? 'success' : 'danger';
    }

    const progress =
      k.goal_cents != null && k.value_cents != null && k.goal_cents > 0
        ? Math.min(k.value_cents / k.goal_cents, 1)
        : undefined;

    return {
      label: k.label ?? meta.label,
      primary,
      sub: k.sub ?? k.target_annotation ?? '',
      ...(subTone ? { subTone } : {}),
      accent: k.color ? kpiColorToAccent(k.color) : meta.accent,
      icon: meta.icon,
      ...(k.live ? { live: true } : {}),
      ...(progress != null
        ? { progress, progressColor: k.color ? kpiColorToAccent(k.color) : meta.accent }
        : {}),
      ...(k.key === 'day_progress' ? { mono: true } : {}),
      ...(k.clickable ? { clickable: true } : {}),
    };
  });
}

const WEEK_KPI_MAP: Record<string, { label: string; icon: KpiItem['icon']; accent: string }> = {
  at_risk: { label: 'At-risk appts', icon: 'alert', accent: 'var(--terracotta)' },
  prep_needed: { label: 'Prep needed', icon: 'crown', accent: 'var(--ai-3)' },
  new_patients: { label: 'New patients', icon: 'team', accent: 'var(--ai-4)' },
  open_slots: { label: 'Open slots', icon: 'stopwatch', accent: 'var(--gold)' },
};

export function adaptWeekKpis(kpis: WeekKpiItem[]): KpiItem[] {
  return kpis.map((k, index) => {
    const key = k.key ?? `week-kpi-${index}`;
    const meta = WEEK_KPI_MAP[key] ?? {
      label: key.replace(/_/g, ' '),
      icon: 'dollar' as const,
      accent: 'var(--ink-2)',
    };
    return {
      label: meta.label,
      primary: String(k.value ?? '—'),
      sub: k.sub ?? '',
      accent: meta.accent,
      icon: meta.icon,
    };
  });
}

export function adaptWeekTitleRange(
  days: Next7Day[],
  meta?: { start_date?: string; end_date?: string },
): CalendarWeekTitleRange | null {
  if (meta?.start_date && meta?.end_date) {
    return formatWeekTitleRange(meta.start_date, meta.end_date);
  }
  const firstDate = days[0]?.date;
  const lastDate = days[days.length - 1]?.date;
  if (firstDate && lastDate) {
    return formatWeekTitleRange(firstDate, lastDate);
  }
  return null;
}

export function adaptWeekHeaderStats(days: Next7Day[]): {
  appointments: string;
  projected: string;
  atRisk: string;
  newPatients: string;
} {
  const apptCount = days.reduce((s, d) => s + (d.cells?.length ?? 0), 0);
  const projected = days.reduce((s, d) => s + (d.production_cents ?? 0), 0);
  const atRisk = days.reduce((s, d) => s + (d.at_risk ?? 0), 0);
  const newPatients = days.reduce((s, d) => s + (d.new_patients ?? 0), 0);
  return {
    appointments: String(apptCount),
    projected: formatMoneyShort(projected),
    atRisk: String(atRisk),
    newPatients: String(newPatients),
  };
}

const FINISHED_STATUSES = new Set(['COMPLETED', 'NO_SHOW', 'BROKEN', 'RESCHED']);

/** Day header stats from the day response: `production_captured` KPI wins, else completed production. */
export function adaptDayHeaderStats(
  cells: CalendarCell[],
  kpis: DayKpiItem[] = [],
): { appointments: string; captured: string; remaining: string; needAttention: string } {
  const live = cells.filter((c) => !c.rebuilding);
  const status = (c: CalendarCell) => c.status?.toUpperCase() ?? 'SCHEDULED';

  const capturedKpi = kpis.find((k) => k.key === 'production_captured');
  const capturedCents =
    capturedKpi?.value_cents ??
    live.filter((c) => status(c) === 'COMPLETED').reduce((s, c) => s + (c.production_cents ?? 0), 0);

  const remaining = live.filter((c) => !FINISHED_STATUSES.has(status(c)));
  const needAttention = remaining.filter((c) => rollupColorToPerf(c.rollup_color) === 'problem');

  return {
    appointments: String(live.length),
    captured: formatMoneyShort(capturedCents),
    remaining: String(remaining.length),
    needAttention: String(needAttention.length),
  };
}

export interface MonthGridAdaptResult {
  data: Record<number, MonthDayRecord>;
  dataByDate: Record<string, MonthDayRecord>;
  gridCells: MonthGridCell[];
  today: { year: number; month: number; day: number };
  weekSummaries: MonthWeekSummary[];
  headerStats: { projected: string; mtd: string; daysLeft: string; newPatients: string };
  kpis: KpiItem[];
  title: { month: string; year: string };
}

const WEEKDAY_TO_DOW: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function rollupColorToPerf(color?: string): MonthDay['perf'] | undefined {
  switch (color?.toLowerCase()) {
    case 'g':
    case 'green':
      return 'great';
    case 'y':
    case 'yellow':
      return 'soft';
    case 'r':
    case 'red':
      return 'problem';
    default:
      return undefined;
  }
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric' });
}

function buildMonthWeekSummaries(
  days: MonthDay[],
  gridCells: MonthGridCell[],
): MonthWeekSummary[] {
  if (gridCells.length < 7) return [];

  const todayIso = new Date().toISOString().slice(0, 10);
  const dayByDate = new Map(days.map((d) => [d.date, d]));
  const summaries: MonthWeekSummary[] = [];

  for (let row = 0; row < gridCells.length; row += 7) {
    const weekCells = gridCells.slice(row, row + 7);
    const inMonthCells = weekCells.filter((c) => !c.off && c.date);
    if (inMonthCells.length === 0) continue;

    let production = 0;
    let goal = 0;
    let daysWithData = 0;
    let note: string | undefined;

    inMonthCells.forEach((cell) => {
      const day = cell.date ? dayByDate.get(cell.date) : undefined;
      if (!day) return;
      production += (day.production_cents ?? 0) / 100;
      goal += (day.goal_cents ?? 0) / 100;
      daysWithData += 1;
      if (day.anomaly && !note) note = day.anomaly;
    });

    const firstDate = inMonthCells[0]?.date;
    const lastDate = inMonthCells[inMonthCells.length - 1]?.date;
    const range =
      firstDate && lastDate
        ? `${formatShortDate(firstDate)} – ${formatShortDate(lastDate)}`
        : '';

    const containsToday = inMonthCells.some((c) => c.date === todayIso);
    const allPast = inMonthCells.every((c) => c.date != null && c.date < todayIso);

    let status: MonthWeekSummary['status'] = 'projected';
    if (containsToday) status = 'current';
    else if (allPast) status = 'done';

    const fillPct = goal > 0 ? Math.round((production / goal) * 100) : 0;

    summaries.push({
      label: `Week ${summaries.length + 1}`,
      range,
      production,
      fillPct,
      days: daysWithData,
      status,
      ...(note ? { note } : {}),
    });
  }

  return summaries;
}

function monthDayToRecord(day: MonthDay): MonthDayRecord {
  const record: MonthDayRecord = {
    production: Math.round((day.production_cents ?? 0) / 100),
    goal: Math.round((day.goal_cents ?? 0) / 100),
    visits: day.visits ?? 0,
    newPatients: day.new_patients ?? 0,
    noShows: day.no_shows ?? 0,
    cancellations: day.cancellations ?? 0,
    partial: day.partial ?? false,
    projected: day.projected ?? false,
  };
  if (day.anomaly) record.anomaly = day.anomaly;
  if (day.highlighted != null) record.highlighted = day.highlighted;
  return record;
}

function normalizeMonthDay(raw: MonthDayRaw): MonthDay {
  const stats = raw.stats ?? {};
  const production_cents =
    raw.production_cents ??
    parseProductionCents(stats.production_total) ??
    0;
  const goal_cents =
    raw.goal_cents ??
    (stats.goal_cents != null
      ? stats.goal_cents
      : stats.goal != null
        ? Math.round(stats.goal * 100)
        : raw.goal != null
          ? Math.round(raw.goal * 100)
          : 0);

  let anomaly: string | undefined;
  if (typeof stats.anomaly === 'string' && stats.anomaly.trim()) {
    anomaly = stats.anomaly;
  } else if (stats.anomaly === true) {
    anomaly = 'Attention needed';
  } else if ((stats.red_count ?? 0) > 0) {
    anomaly = `${String(stats.red_count)} at risk`;
  }

  const perf =
    raw.perf ??
    rollupColorToPerf(stats.rollup_color) ??
    'good';

  return {
    date: raw.date,
    ...(raw.is_in_month != null ? { is_in_month: raw.is_in_month } : {}),
    ...(raw.weekday != null ? { weekday: raw.weekday } : {}),
    production_cents,
    goal_cents,
    visits: raw.visits ?? stats.appointment_count ?? 0,
    new_patients: raw.new_patients ?? stats.newPatients ?? stats.new_patients ?? 0,
    no_shows: raw.no_shows ?? stats.noShows ?? stats.no_shows ?? 0,
    cancellations: raw.cancellations ?? stats.cancellations ?? 0,
    perf,
    projected: raw.projected ?? stats.projected ?? false,
    partial: raw.partial ?? stats.partial ?? false,
    closed: raw.closed ?? raw.weekday === 'Sun',
    ...(anomaly ? { anomaly } : {}),
    ...((stats.red_count ?? 0) > 0 ? { red_count: stats.red_count } : {}),
    ...(raw.highlighted != null ? { highlighted: raw.highlighted } : {}),
  };
}

function normalizeMonthSummary(raw: CalendarMonthResponseRaw): CalendarMonthSummary {
  const summary = raw.monthly_summary ?? raw.summary ?? {};
  const wire = summary as CalendarMonthSummaryRaw & CalendarMonthSummary;
  return {
    production_cents:
      wire.production_cents ?? parseProductionCents(wire.total_production) ?? 0,
    goal_cents: wire.goal_cents ?? 0,
    new_patients: wire.new_patients ?? wire.newPatients ?? 0,
  };
}

export function adaptCalendarMonthResponse(raw: CalendarMonthResponseRaw): CalendarMonthResponse {
  return {
    ...(raw.meta ? { meta: raw.meta } : {}),
    summary: normalizeMonthSummary(raw),
    days: (raw.days ?? []).map(normalizeMonthDay),
  };
}

export function adaptMonthGrid(
  days: MonthDay[] = [],
  summary: CalendarMonthSummary = { production_cents: 0, goal_cents: 0, new_patients: 0 },
  year: number,
  month: number,
): MonthGridAdaptResult {
  const now = new Date();
  const today = {
    year: now.getFullYear(),
    month: now.getMonth(),
    day: now.getDate(),
  };

  const data: Record<number, MonthDayRecord> = {};
  const dataByDate: Record<string, MonthDayRecord> = {};
  const gridCells: MonthGridCell[] = [];

  days.forEach((d) => {
    if (!d.date) return;
    const dayNum = Number(d.date.split('-')[2]);
    if (!dayNum) return;

    gridCells.push({
      dayNum,
      off: d.is_in_month === false,
      dow:
        (d.weekday ? WEEKDAY_TO_DOW[d.weekday] : undefined) ??
        new Date(`${d.date}T12:00:00`).getDay(),
      date: d.date,
    });

    if (d.is_in_month === false) return;

    const record = monthDayToRecord(d);
    dataByDate[d.date] = record;
    data[dayNum] = record;
  });

  const monthName = new Date(year, month - 1, 1).toLocaleString(undefined, { month: 'long' });
  const goalDollars = (summary.goal_cents ?? 0) / 100;
  const prodDollars = (summary.production_cents ?? 0) / 100;

  const kpis: KpiItem[] = [
    {
      label: `Projected · ${monthName}`,
      primary: formatMoneyShort(summary.production_cents ?? 0),
      sub: summary.goal_cents ? `of ${formatMoneyShort(summary.goal_cents)} goal` : 'this month',
      ...(summary.goal_cents
        ? { subTone: prodDollars >= goalDollars ? ('success' as const) : ('danger' as const) }
        : {}),
      accent: 'var(--success)',
      icon: 'dollar',
      progress: goalDollars > 0 ? Math.min(prodDollars / goalDollars, 1) : 0,
      progressColor: 'var(--success)',
    },
    {
      label: 'New patients',
      primary: String(summary.new_patients ?? 0),
      sub: 'this month',
      accent: 'var(--ai-4)',
      icon: 'team',
    },
  ];

  const daysInMonth = new Date(year, month, 0).getDate();
  const daysLeft = Math.max(
    0,
    daysInMonth -
      (today.month === month - 1 && today.year === year ? today.day : daysInMonth),
  );

  return {
    data,
    dataByDate,
    gridCells,
    today,
    weekSummaries: buildMonthWeekSummaries(days, gridCells),
    headerStats: {
      projected: formatMoneyShort(summary.production_cents ?? 0),
      mtd: formatMoneyShort(summary.production_cents ?? 0),
      daysLeft: String(daysLeft),
      newPatients: String(summary.new_patients ?? 0),
    },
    kpis,
    title: { month: monthName, year: String(year) },
  };
}
